import { EXPLORE_UNKNOWN_HEART_METERS } from '../config/exploreUnknownHearts';
import { EXPLORE_UNKNOWN_EP_METERS } from '../config/exploreUnknownEmergencyProtocol';

/**
 * @typedef {'darkKnowledge' | 'heart' | 'ep' | 'rewrite'} ExploreUnknownRewardPopupType
 */

/**
 * @param {unknown} winRewards
 * @param {{ id: string } | null | undefined} winReward
 * @returns {Array<object>}
 */
export function extractExploreUnknownRewardPopupsFromWinRewards(winRewards, winReward = null) {
  const rewards = [];
  if (Array.isArray(winRewards)) {
    for (const entry of winRewards) {
      if (entry && typeof entry === 'object') {
        rewards.push(entry);
      }
    }
  }
  if (rewards.length === 0 && winReward && typeof winReward === 'object') {
    rewards.push(winReward);
  }

  /** @type {Array<object>} */
  const popups = [];
  let seq = 0;

  for (const reward of rewards) {
    if (reward.darkKnowledgeSetCompleted === true) {
      const rewardType = reward.darkKnowledgeRewardType;
      if (rewardType === 'order') {
        const orderId = Number(reward.darkKnowledgeOrderId);
        if (Number.isNaN(orderId)) {
          throw new Error(
            'extractExploreUnknownRewardPopupsFromWinRewards: darkKnowledge order completion missing darkKnowledgeOrderId'
          );
        }
        popups.push({
          type: 'darkKnowledge',
          popupId: `eu-minigame-dk-order-${seq}`,
          rewardType: 'order',
          orderId,
        });
      } else if (rewardType === 'billy_bucks') {
        const amount = Number(reward.darkKnowledgeBillyBucksAmount);
        if (!Number.isFinite(amount) || amount <= 0) {
          throw new Error(
            'extractExploreUnknownRewardPopupsFromWinRewards: darkKnowledge billy_bucks completion missing amount'
          );
        }
        popups.push({
          type: 'darkKnowledge',
          popupId: `eu-minigame-dk-bb-${seq}`,
          rewardType: 'billy_bucks',
          amount,
        });
      } else {
        throw new Error(
          `extractExploreUnknownRewardPopupsFromWinRewards: darkKnowledgeSetCompleted without darkKnowledgeRewardType (${String(rewardType)})`
        );
      }
      seq += 1;
    }

    if (reward.heartSetCompleted === true) {
      const heartId = reward.heartBonusId;
      if (typeof heartId !== 'string') {
        throw new Error('extractExploreUnknownRewardPopupsFromWinRewards: heart set complete missing heartBonusId');
      }
      const heartMeter = EXPLORE_UNKNOWN_HEART_METERS.find((m) => m.id === heartId);
      if (!heartMeter) {
        throw new Error(
          `extractExploreUnknownRewardPopupsFromWinRewards: unknown heartBonusId ${JSON.stringify(heartId)}`
        );
      }
      const healthBonus = Number(reward.heartHealthBonus);
      if (!Number.isFinite(healthBonus) || healthBonus <= 0) {
        throw new Error('extractExploreUnknownRewardPopupsFromWinRewards: heart set complete missing heartHealthBonus');
      }
      popups.push({
        type: 'heart',
        popupId: `eu-minigame-heart-${heartId}-${seq}`,
        heartId,
        imageSrc: heartMeter.image,
        variant: heartMeter.variant,
        i18nKey: heartMeter.i18nKey,
        healthBonus,
      });
      seq += 1;
    }

    if (reward.epSetCompleted === true) {
      const epId = reward.epBonusId;
      if (typeof epId !== 'string') {
        throw new Error('extractExploreUnknownRewardPopupsFromWinRewards: EP set complete missing epBonusId');
      }
      const epMeter = EXPLORE_UNKNOWN_EP_METERS.find((m) => m.id === epId);
      if (!epMeter) {
        throw new Error(`extractExploreUnknownRewardPopupsFromWinRewards: unknown epBonusId ${JSON.stringify(epId)}`);
      }
      const newCharges = Number(reward.epCharges);
      if (!Number.isFinite(newCharges) || newCharges < 1) {
        throw new Error('extractExploreUnknownRewardPopupsFromWinRewards: EP set complete missing valid epCharges');
      }
      popups.push({
        type: 'ep',
        popupId: `eu-minigame-ep-${epId}-${seq}`,
        epId,
        imageSrc: epMeter.image,
        variant: epMeter.variant,
        i18nKey: epMeter.i18nKey,
        newCharges,
      });
      seq += 1;
    }

    if (reward.rewriteExperienceSetCompleted === true) {
      const newCharges = Number(reward.rewriteExperienceCharges);
      if (!Number.isFinite(newCharges) || newCharges < 1) {
        throw new Error(
          'extractExploreUnknownRewardPopupsFromWinRewards: rewrite set complete missing valid rewriteExperienceCharges'
        );
      }
      popups.push({
        type: 'rewrite',
        popupId: `eu-minigame-rewrite-${seq}`,
        newCharges,
      });
      seq += 1;
    }
  }

  return popups;
}
